import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showManufacturerItem?: boolean;
    showLabel?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: InjectableCss;
    erpNumberGridItem?: GridItemProps;
    containerItem?: InjectableCss;
    erpNumberLabelText?: InjectableCss;
    erpNumberValueText?: InjectableCss;
    customerNameGridItem?: InjectableCss;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: InjectableCss;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    erpNumberLabelText: {
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
        `,
    },
    customerNameLabelText: {
        css: css`
            flex: 0 0 auto;
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
        `,
    },
    manufacturerItemLabelText: {
        css: css`
            flex: 0 0 auto;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
        `,
    },
    container: {
        css: css`
            border: 1.5px dotted green;
            border-right: none;
            border-left: none;
            display: flex;
            flex-wrap: wrap;
            flex-basis: 100%;
        `,
    },
    containerItem: {
        css: css`
            flex: 50%;
            line-height: 1.2;
            font-size: 14px;
            padding: 5px 15px 5px 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex: auto;
                        `,
                        css`
                            flex: 50%;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showLabel = true,
    showCustomerName = true,
    showManufacturerItem = true,
    extendedStyles,
    showProductNumber = true,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.containerItem}>
                    <Typography as="p" {...styles.erpNumberLabelText}>
                        {translate("CBPN:")}
                    </Typography>
                    <Typography as="p" {...styles.erpNumberValueText}>
                        {productNumber}
                    </Typography>
                </StyledWrapper>
                {displayCustomerNumber && (
                    <StyledWrapper {...styles.containerItem}>
                        <Typography as="p" {...styles.customerNameLabelText}>
                            {translate("CPN:")}
                        </Typography>
                        <Typography as="p" {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                            {customerProductNumber}
                        </Typography>
                    </StyledWrapper>
                )}
                {displayManufacturerNumber && (
                    <StyledWrapper {...styles.containerItem}>
                        <Typography as="p" {...styles.manufacturerItemLabelText}>
                            {translate("MPN:")}{" "}
                        </Typography>
                        <Typography as="p" {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                            {manufacturerItem}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {/* <GridContainer {...styles.container}>
                {showProductNumber && (
                    <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                        {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                            </VisuallyHidden>
                        )}
                        <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                            {productNumber}
                        </Typography>
                    </GridItem>
                )}
                {displayCustomerNumber && (
                    <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                        {showLabel && (
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        )}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                            </VisuallyHidden>
                        )}
                        <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                            {customerProductNumber}
                        </Typography>
                    </GridItem>
                )}
                {displayManufacturerNumber && (
                    <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                        {showLabel && (
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>
                        )}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")} </Typography>
                            </VisuallyHidden>
                        )}
                        <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                            {manufacturerItem}
                        </Typography>
                    </GridItem>
                )}
            </GridContainer> */}
        </>
    );
};

export default ProductPartNumbers;
